import { useMemo, useState } from "react";

import type { FileItem } from "../api/client";
import { ThumbnailGrid } from "../components/ThumbnailGrid";
import { UploadQueueRow } from "../components/UploadQueueRow";
import { UploadZone } from "../components/UploadZone";
import { useDeleteFile } from "../hooks/useDeleteFile";
import { useMyFiles } from "../hooks/useMyFiles";
import { useUploadQueue } from "../hooks/useUploadQueue";

export function UserView() {
  const queue = useUploadQueue();
  const my = useMyFiles("desc");
  const del = useDeleteFile();
  const [dragActive, setDragActive] = useState(false);

  const items = useMemo<FileItem[]>(
    () => (my.data?.pages ?? []).flatMap((p) => p.items),
    [my.data],
  );

  const onDelete = (f: FileItem) => {
    if (window.confirm(`Poistetaanko ${f.original_name}?`)) {
      del.mutate(f.id);
    }
  };

  const acceptDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const list = e.dataTransfer.files;
    if (!list || list.length === 0) return;
    const files: File[] = [];
    for (const f of list) files.push(f);
    queue.enqueue(files);
  };

  return (
    <section
      className="relative flex h-full flex-col"
      onDragEnter={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(e) => {
        // only deactivate when leaving the section, not its children
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragActive(false);
      }}
      onDrop={acceptDrop}
    >
      {dragActive && (
        <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-ink/80 text-white">
          <span className="rounded border-2 border-dashed border-white/60 px-6 py-3 text-lg font-medium">
            Tiputa kuvat tähän
          </span>
        </div>
      )}
      <header className="border-b border-ink/10 p-3">
        <h2 className="text-sm font-semibold">Kuvasi</h2>
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        <UploadZone onFiles={queue.enqueue} />

        {queue.items.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/60">
                Käynnissä
              </h3>
              <button
                type="button"
                className="text-xs text-ink/60 underline"
                onClick={queue.clearFinished}
              >
                Tyhjennä valmiit
              </button>
            </div>
            {queue.items.map((it) => (
              <UploadQueueRow key={it.id} item={it} onAbort={queue.abort} />
            ))}
          </div>
        )}

        <div className="mt-6">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink/60">
            Lähettämäsi kuvat
          </h3>
          {my.isLoading ? (
            <p className="p-6 text-center text-sm text-ink/60">Ladataan…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-center text-sm text-ink/60">Et ole vielä lähettänyt kuvia.</p>
          ) : (
            <ThumbnailGrid
              items={items}
              hasNextPage={!!my.hasNextPage}
              onLoadMore={() => my.fetchNextPage()}
              renderTileOverlay={(f) => (
                <span
                  role="button"
                  tabIndex={0}
                  className="absolute right-1 top-1 z-10 rounded bg-black/70 px-2 py-1 text-xs text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(f);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onDelete(f);
                    }
                  }}
                >
                  Poista
                </span>
              )}
            />
          )}
        </div>
      </div>
    </section>
  );
}
